/**
 * TeamAbilityPanel
 * L4-TAP-003: Responsive container for creature ability cards
 *
 * Desktop: Left side panel (width scales with creature count)
 * Mobile portrait: Fixed bottom bar (height scales with creature count)
 * Mobile landscape: Hidden (insufficient vertical space)
 *
 * Dynamically sizes cards based on creature count:
 * fewer creatures = larger cards, more creatures = smaller to fit.
 */

import React, { useMemo, useRef } from 'react';
import type { CombatState, PlayerId } from '@drawn-of-war/shared';
import type { AxialCoordinate } from '@drawn-of-war/shared';
import { CreatureAbilityCard } from './CreatureAbilityCard';
import type { AbilityDisplayData } from './CreatureAbilityCard';
import styles from './TeamAbilityPanel.module.css';

export interface TeamAbilityPanelProps {
  combatState: CombatState | null;
  currentPlayerId: PlayerId;
  isMobile: boolean;
  isPortrait: boolean;
  onCreatureClick: (unitId: string, position: AxialCoordinate) => void;
}

/**
 * Sizing tiers based on creature count.
 * Fewer creatures = larger, more visual detail.
 * More creatures = compact to fit the available space.
 */
interface SizingConfig {
  panelWidth: number;     // Desktop panel width (px)
  panelHeight: number;    // Mobile panel height (px)
  portraitSize: number;   // Portrait circle diameter (px)
  iconSize: number;       // Ability icon size (px)
  fontSize: number;       // Name font size (px)
  hpBarHeight: number;    // HP bar height (px)
  gap: number;            // Gap between cards (px)
  cardPadding: number;    // Card internal padding (px)
  showName: boolean;      // Whether to show creature name
}

function getSizingConfig(count: number, isCompact: boolean): SizingConfig {
  if (isCompact) {
    // Mobile: horizontal layout — sizing based on fitting cards in screen width
    if (count <= 3) {
      return { panelWidth: 0, panelHeight: 88, portraitSize: 36, iconSize: 24, fontSize: 10, hpBarHeight: 3, gap: 6, cardPadding: 6, showName: true };
    }
    if (count <= 5) {
      return { panelWidth: 0, panelHeight: 80, portraitSize: 30, iconSize: 20, fontSize: 9, hpBarHeight: 3, gap: 4, cardPadding: 5, showName: false };
    }
    return { panelWidth: 0, panelHeight: 72, portraitSize: 24, iconSize: 18, fontSize: 9, hpBarHeight: 2, gap: 3, cardPadding: 4, showName: false };
  }

  // Desktop: vertical layout — sizing based on fitting cards in panel height
  if (count <= 3) {
    return { panelWidth: 150, panelHeight: 0, portraitSize: 52, iconSize: 32, fontSize: 12, hpBarHeight: 5, gap: 8, cardPadding: 10, showName: true };
  }
  if (count <= 5) {
    return { panelWidth: 130, panelHeight: 0, portraitSize: 40, iconSize: 28, fontSize: 11, hpBarHeight: 4, gap: 6, cardPadding: 8, showName: true };
  }
  if (count <= 6) {
    return { panelWidth: 120, panelHeight: 0, portraitSize: 34, iconSize: 24, fontSize: 10, hpBarHeight: 4, gap: 4, cardPadding: 6, showName: true };
  }
  // 7-8 creatures: most compact desktop
  return { panelWidth: 110, panelHeight: 0, portraitSize: 28, iconSize: 22, fontSize: 10, hpBarHeight: 3, gap: 3, cardPadding: 5, showName: true };
}

export function TeamAbilityPanel({
  combatState,
  currentPlayerId,
  isMobile,
  isPortrait,
  onCreatureClick,
}: TeamAbilityPanelProps): JSX.Element | null {
  // Stable unit ordering: capture order from first non-null state
  const unitOrderRef = useRef<string[] | null>(null);

  // Filter to player's creatures
  const playerCreatures = useMemo(() => {
    if (!combatState?.units) return [];
    return combatState.units.filter((u) => u.ownerId === currentPlayerId);
  }, [combatState?.units, currentPlayerId]);

  // Establish stable ordering on first render with data
  if (playerCreatures.length > 0 && unitOrderRef.current === null) {
    unitOrderRef.current = playerCreatures.map((u) => u.unitId);
  }

  // Sort creatures by stable order
  const sortedCreatures = useMemo(() => {
    if (!unitOrderRef.current) return playerCreatures;
    const orderMap = new Map(unitOrderRef.current.map((id, i) => [id, i]));
    return [...playerCreatures].sort((a, b) => {
      const aIdx = orderMap.get(a.unitId) ?? 999;
      const bIdx = orderMap.get(b.unitId) ?? 999;
      return aIdx - bIdx;
    });
  }, [playerCreatures]);

  // Don't render if no state or no creatures
  if (!combatState || sortedCreatures.length === 0) return null;

  // Mobile landscape: hidden
  if (isMobile && !isPortrait) return null;

  const isCompact = isMobile && isPortrait;
  const sizing = getSizingConfig(sortedCreatures.length, isCompact);

  // CSS custom properties for dynamic sizing
  const panelStyle: React.CSSProperties = isCompact
    ? { height: sizing.panelHeight, gap: sizing.gap }
    : { width: sizing.panelWidth, gap: sizing.gap };

  const panelClass = isCompact ? styles.panelMobile : styles.panelDesktop;

  return (
    <div className={panelClass} style={panelStyle} role="region" aria-label="Team abilities">
      {sortedCreatures.map((creature) => {
        const abilities: AbilityDisplayData[] = (creature.dynamicAbilities || []).map(
          (a: any) => ({
            id: a.id || a.abilityId || '',
            name: a.name || '',
            element: a.element || 'physical',
            delivery: a.delivery,
            cooldownRemaining: a.cooldownRemaining || 0,
            cooldownTicks: a.cooldownTicks || a.cooldownTotal || 0,
          })
        );

        return (
          <CreatureAbilityCard
            key={creature.unitId}
            unitId={creature.unitId}
            name={creature.name}
            portraitUrl={creature.portraitUrl}
            health={creature.health}
            maxHealth={creature.maxHealth}
            status={creature.status}
            abilities={abilities}
            isCompact={isCompact}
            sizing={sizing}
            onClick={() => onCreatureClick(creature.unitId, creature.position)}
          />
        );
      })}
    </div>
  );
}

export default TeamAbilityPanel;
